import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Typography } from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { DecisionNode } from 'lib/types/models';
import {
  CategorizedNodes,
  Node,
} from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import RangesAssign from '../newAssignation/RangesAssignation';

type Props = {
  openEditNode: boolean;
  setOpenEditNode: (open: boolean) => void;
  editingNode: Node;
  setEditingNode: (node: Node) => void;
  decisionNodes: DecisionNode[];
  categorizedNodes: CategorizedNodes;
  decisionNodesAssignation: Node[];
  setDecisionNodesAssignation: (nodes: Node[]) => void;
};

const EditAssignation = (props: Props) => {
  const {
    openEditNode,
    setOpenEditNode,
    editingNode,
    setEditingNode,
    decisionNodes,
    categorizedNodes,
    decisionNodesAssignation,
    setDecisionNodesAssignation,
  } = props;
  const { id } = useParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [operation, setOperation] = useState('gt');

  const saveNode = () => {
    const nodes = [...decisionNodesAssignation];
    nodes[nodes.findIndex((x) => x.id === editingNode.id)] = editingNode;
    setDecisionNodesAssignation(nodes);
    setOpenEditNode(false);
  };

  const handleChangeCategory = (e: SelectChangeEvent<string>) => {
    setSelectedCategory(e.target.value);
  };
  const setUpRangeForBoolean = () => {
    const ranges = [
      {
        value: '0',
        decisionNodeId: editingNode.id,
        nodeId: 0,
      },
      {
        value: '1',
        decisionNodeId: editingNode.id,
        nodeId: 1,
      },
    ];
    return ranges;
  };

  const handleChangeOrigin = (e: SelectChangeEvent<number | null>) => {
    const { value, name } = e.target;
    if (name === 'originNodeId') {
      const node = decisionNodes.find((x) => x.id === Number(value));

      return setEditingNode({
        ...editingNode,
        [String(name)]: value,
        value: '',
        type: node?.type || null,
        operation: node?.operation || 'gt',
        description: node?.description || '',
        name: node?.name || '',
        id: node?.id || 0,
        ranges: node?.type === 'boolean' ? setUpRangeForBoolean() : [],
      });
    }
    setEditingNode({ ...editingNode, [name]: value });
  };

  const availableNodeForSelection = () => {
    const selectedIds = decisionNodesAssignation.map((x) => x.originNodeId);
    return decisionNodes.filter((x) => {
      if (x.type === 'final') return false;
      if (x.id === Number(editingNode.originNodeId)) return true;
      return (
        !selectedIds.includes(x.id) &&
        x.category === selectedCategory &&
        Number(x.subDecisionEngineId) !== Number(id)
      );
    });
  };

  const availableFinalNodeForSelection = () => {
    return decisionNodes.filter((x) => x.type === 'final');
  };

  const addRange = () => {
    const range = {
      value: '',
      decisionNodeId: editingNode.id,
    };
    setEditingNode({ ...editingNode, ranges: [...editingNode.ranges, range] });
  };

  const handleChangeRange = (e: SelectChangeEvent<number>, index: number) => {
    const { value } = e.target;
    const { name } = e.target;
    const ranges = editingNode.ranges.map((x, i) => {
      if (i === index) return { ...x, [name]: value };
      return x;
    });
    setEditingNode({ ...editingNode, ranges });
  };

  const deleteRange = (index: number) => {
    const ranges = editingNode.ranges.filter((x, i) => i !== index);
    ranges[ranges.length - 1].max = null;
    setEditingNode({ ...editingNode, ranges });
  };

  useEffect(() => {
    const category = Object.keys(categorizedNodes).find((x) =>
      categorizedNodes[x].some((y) => y.id === editingNode.originNodeId),
    );
    if (category) setSelectedCategory(category);
    setOperation(editingNode.operation);
  }, [editingNode]);

  return (
    <Dialog
      maxWidth="md"
      open={openEditNode}
      onClose={() => setOpenEditNode(false)}
    >
      <DialogTitle>Agregar evaluación</DialogTitle>
      <DialogContent style={{ width: '500px' }}>
        <Typography variant="body1" gutterBottom>
          Acá podrás definir una nueva evaluación dentro de la matriz.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <b>Evaluación a agregar</b>
        </Typography>
        <div style={{ padding: '10px 0px' }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="filter-label">Categorías</InputLabel>
            <Select
              labelId="filter-label"
              value={selectedCategory}
              onChange={handleChangeCategory}
              label="Categorías"
            >
              {Object.keys(categorizedNodes).map((x, index) => (
                <MenuItem key={index} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: '10px 0px' }}>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={selectedCategory === ''}
          >
            <InputLabel id="filter-label">Evaluaciones disponibles</InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              name="originNodeId"
              value={editingNode.originNodeId}
              onChange={handleChangeOrigin}
              label="Evaluaciones disponibles"
            >
              {availableNodeForSelection().map((x, index) => (
                <MenuItem key={index} value={x.id}>
                  {x.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {editingNode.originNodeId && (
          <div style={{ padding: '20ox' }}>
            <Typography variant="body1" gutterBottom>
              {editingNode.description}
            </Typography>
          </div>
        )}
        {editingNode.type !== 'decisionEngine' && editingNode.originNodeId && (
          <RangesAssign
            newNode={editingNode}
            setNewNode={setEditingNode}
            addRange={addRange}
            handleChangeRange={handleChangeRange}
            deleteRange={deleteRange}
            availableFinalNodeForSelection={availableFinalNodeForSelection}
            operation={operation}
            setOperation={setOperation}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={() => setOpenEditNode(false)}
          color="secondary"
          variant="contained"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => saveNode()}
          color="primary"
          variant="contained"
        >
          Confirmar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssignation;
